import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Replace, X, Ban } from "lucide-react";

/** v207 — Manage remembered substitutes + discontinued flag on an item. */
const SubstitutesCard = ({ itemId, ids, discontinued, replacedById, onChange }) => {
  const [stock, setStock] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/stock").then(({ data }) => setStock(data)).catch(() => {});
  }, []);
  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return stock
      .filter((s) => s.id !== itemId && !ids.includes(s.id))
      .filter((s) => `${s.name} ${s.sku || ""}`.toLowerCase().includes(t))
      .slice(0, 6);
  }, [stock, q, ids, itemId]);

  return (
    <div className="nb-card-sm p-4" data-testid="stock-substitutes-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-2">
        <Replace size={12} strokeWidth={2.5} /> Substitutes
      </div>
      <label className="flex items-center gap-2 text-sm font-bold mb-2">
        <input
          type="checkbox"
          data-testid="stock-discontinued-toggle"
          checked={!!discontinued}
          onChange={(e) => onChange({ discontinued: e.target.checked, replaced_by_id: e.target.checked ? replacedById : null })}
          className="w-4 h-4"
        />
        <Ban size={13} strokeWidth={2.5} className="text-[#8A1F1F]" />
        Discontinued — hide from Reorder Suggestions
        {discontinued && replacedById && byId[replacedById] && (
          <span className="text-[11px] font-normal text-[#607087]">(replaced by {byId[replacedById].name})</span>
        )}
      </label>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="stock-substitutes-list">
        {ids.length === 0 && <span className="text-xs text-[#607087]">No substitutes recorded yet.</span>}
        {ids.map((id) => (
          <span key={id} data-testid={`stock-substitute-chip-${id}`} className="inline-flex items-center gap-1 border-2 border-[#0A3A7A] bg-[#EAF5FB] rounded-full pl-2.5 pr-1 py-0.5 text-xs font-bold">
            {byId[id]?.name || "Unknown item"}
            <button
              type="button"
              data-testid={`stock-substitute-remove-${id}`}
              onClick={() => onChange({ substitute_ids: ids.filter((x) => x !== id) })}
              className="rounded-full hover:bg-white p-0.5"
              aria-label="remove substitute"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
      <input
        data-testid="stock-substitute-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Add a substitute — search inventory…"
        className="nb-input py-1.5 text-sm w-full"
      />
      {matches.length > 0 && (
        <div className="flex flex-col gap-1 mt-1">
          {matches.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`stock-substitute-option-${s.id}`}
              onClick={() => {
                onChange({ substitute_ids: [...ids, s.id] });
                setQ("");
              }}
              className="text-left nb-card-sm px-2.5 py-1.5 bg-white hover:bg-[#EAF5FB] text-sm font-bold truncate"
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubstitutesCard;
